import {FormControl, FormGroup} from '@angular/forms';

import {HttpStatusCodesEnum, ServerResponse, ServerResponseData, SesService, UserCategoryTypesEnum, UserSettingsInputModel} from '@spa/api';
import {BaseControl, CheckboxControl, SelectControl} from 'appShared/classes/controls';

import {
    ClientUsersNameMinLength,
    ClientUsersUsageSettingsFormCboeLocationIdSelector,
    ClientUsersUsageSettingsFormCboeRecipientIdSelector,
    ClientUsersUsageSettingsFormControls,
    ClientUsersUsageSettingsFormIsNyseAgreementSignedSelector,
    ClientUsersUsageSettingsFormIsOpraAgreementSignedSelector,
    ClientUsersUsageSettingsFormMarketDataUsageCategorySelector,
    ClientUsersUsageSettingsFormMaxConcurrentConnectionsSelector,
    ClientUsersUsageSettingsFormMaxL1SubscriptionsSelector,
    ClientUsersUsageSettingsFormMaxL2SubscriptionsSelector,
} from 'appAssets/descriptors';
import {UserSettingsInterface} from '@spa/api/lib/interfaces';
import {switchMap} from 'rxjs/internal/operators';
import {Helper} from 'appShared/classes';
import {ClientUsersUsageSettingsFormErrorStatesEnum} from 'appEntries/panel/enums';

export class ClientUsersUsageSettingsForm {
    private readonly clientUser: string;
    private readonly isCompanyEmployee: boolean;
    private clientUserUsageSettings: UserSettingsInterface;
    public form: FormGroup;
    public sesService: SesService;
    public controls: (BaseControl<string> | SelectControl | CheckboxControl)[];

    constructor(
        isCompanyEmployee: boolean,
        clientUser: string,
        clientUserUsageSettings: UserSettingsInterface,
        sesService: SesService,
    ) {
        this.isCompanyEmployee = isCompanyEmployee;
        this.clientUser = clientUser;
        this.clientUserUsageSettings = clientUserUsageSettings;
        this.sesService = sesService;
    }

    updateFormControlsByUserSettings(userSettings: UserSettingsInterface): void {
        this.clientUserUsageSettings = userSettings;
        this.initForm();
    }

    initForm(): void {

        this.form = new FormGroup({});

        this.controls = ClientUsersUsageSettingsFormControls(
            this.isCompanyEmployee,
            Number.isInteger(this.clientUser),
            this.clientUserUsageSettings?.nyseAgreementSigned,
            this.clientUserUsageSettings?.opraAgreementSigned
        ).map((control: BaseControl<string> | SelectControl | CheckboxControl) => {
            switch (control.name) {
                case ClientUsersUsageSettingsFormMaxConcurrentConnectionsSelector:
                    control.value = this.clientUserUsageSettings?.maxConcurrentConnections;
                    break;
                case ClientUsersUsageSettingsFormMaxL1SubscriptionsSelector:
                    control.value = this.clientUserUsageSettings?.maxL1Subscriptions;
                    break;
                case ClientUsersUsageSettingsFormMaxL2SubscriptionsSelector:
                    control.value = this.clientUserUsageSettings?.maxL2Subscriptions;
                    break;
                case ClientUsersUsageSettingsFormMarketDataUsageCategorySelector:
                    control.value = this.clientUserUsageSettings?.usageCategory;
                    break;
                case ClientUsersUsageSettingsFormCboeRecipientIdSelector:
                    control.value = this.clientUserUsageSettings?.cboeRecipientId;
                    break;
                case ClientUsersUsageSettingsFormCboeLocationIdSelector:
                    control.value = this.clientUserUsageSettings?.cboeLocationId;
                    break;
                case ClientUsersUsageSettingsFormIsNyseAgreementSignedSelector:
                    control.value = this.clientUserUsageSettings?.nyseAgreementSigned;
                    break;
                case ClientUsersUsageSettingsFormIsOpraAgreementSignedSelector:
                    control.value = this.clientUserUsageSettings?.opraAgreementSigned;
                    break;
            }

            this.form.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((key: string) => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((errorKey: string) => {
                        return messageFunction(control.placeholder, ClientUsersNameMinLength)[errorKey];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): ServerResponse<UserSettingsInterface> {

        if (!this.form.dirty) {
            return null;
        }

        const isClientUser = Number.isInteger(this.clientUser);
        const maxConcurrentConnections = this.form.value[ClientUsersUsageSettingsFormMaxConcurrentConnectionsSelector];
        const maxL1Subscriptions = this.form.value[ClientUsersUsageSettingsFormMaxL1SubscriptionsSelector];
        const maxL2Subscriptions = this.form.value[ClientUsersUsageSettingsFormMaxL2SubscriptionsSelector];
        const cboeRecipientId = this.form.value[ClientUsersUsageSettingsFormCboeRecipientIdSelector];
        const cboeLocationtId = this.form.value[ClientUsersUsageSettingsFormCboeLocationIdSelector];
        const isNyseAgreementSigned = this.form.value[ClientUsersUsageSettingsFormIsNyseAgreementSignedSelector];
        const isOpraAgreementSigned = this.form.value[ClientUsersUsageSettingsFormIsOpraAgreementSignedSelector];
        const usageCategory = isClientUser
            ? this.form.value[ClientUsersUsageSettingsFormMarketDataUsageCategorySelector]
            : UserCategoryTypesEnum.Service;

        const nyseAgreementSigned = usageCategory === UserCategoryTypesEnum.Pro ? isNyseAgreementSigned : null;
        const opraAgreementSigned = usageCategory === UserCategoryTypesEnum.Pro ? isOpraAgreementSigned : null;
        const inputModel = new UserSettingsInputModel(
            this.clientUser,
            maxConcurrentConnections,
            maxL1Subscriptions,
            maxL2Subscriptions,
            usageCategory,
            isClientUser ? cboeRecipientId : null,
            isClientUser ? cboeLocationtId : null,
            nyseAgreementSigned,
            opraAgreementSigned
        );

        return this.sesService.updateUserSettings(this.clientUser, inputModel)
        .pipe(switchMap((response: ServerResponseData<UserSettingsInterface>) => {
            if (response.status !== HttpStatusCodesEnum.Ok) {
                return Helper.errorsHelper().createErrorResponseObservable<UserSettingsInterface>(
                    response,
                    ClientUsersUsageSettingsFormErrorStatesEnum.UpdateClientUsersUsageSettings
                );
            }

            this.clientUserUsageSettings.maxConcurrentConnections = maxConcurrentConnections;
            this.clientUserUsageSettings.maxL1Subscriptions = maxL1Subscriptions;
            this.clientUserUsageSettings.maxL2Subscriptions = maxL2Subscriptions;
            this.clientUserUsageSettings.usageCategory = usageCategory;
            this.clientUserUsageSettings.cboeRecipientId = cboeRecipientId;
            this.clientUserUsageSettings.cboeLocationId = cboeLocationtId;
            this.clientUserUsageSettings.nyseAgreementSigned = nyseAgreementSigned;

            return Helper.createSuccessResponseObservable<UserSettingsInterface>(response.data as UserSettingsInterface);

        }));
    }
}
